import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { trans, useSoyuzLocales, useSoyuzFlag } from '@spotahome/soyuz/client';
import { Mediator } from '@spotahome/soyuz-mediator';
import { ZENDESK_SUPPORT } from '@spotahome/customer-graphql-modules/dist/constants';

import createZendeskTicket from '@spotahome/ui-library/src/tenant/mutations/zendesk';

import LazyLoginSignUpModal from '@spotahome/ui-library/src/tenant/components/LoginSignupModal';

import graphqlClient from '../../graphql/client';

import {
  traditionalSignup,
  traditionalLogin,
  traditionalForgotPassword
} from '../../api/traditional-login-client';

const reloadPage = () => {
  document.location.reload();
};

const scrollTop = () => window.scrollTo({ top: 0 });

const LoginModal = ({
  onTraditionalLoginSuccess = reloadPage,
  onTraditionalSignupSuccess = reloadPage,
  deviceViewType = ''
}) => {
  const { current: locale } = useSoyuzLocales();
  const [error, setError] = useState(null);
  const hideTraditionalAuth = useSoyuzFlag('HideTraditionalAuth').isOn();
  const hideFacebookOauth = useSoyuzFlag('HideFacebookOauth').isOn();
  const hideGoogleOauth = useSoyuzFlag('HideGoogleOauth').isOn();

  const handleTraditionalLogin = async ({ email, password }) => {
    try {
      setError(null);
      await traditionalLogin({ email, password });
      onTraditionalLoginSuccess();
    } catch (e) {
      setError(trans('login.error'));
      console.error(e);
    }
  };

  const handleTraditionalSignup = async ({
    name,
    email,
    password,
    marketingOptIn
  }) => {
    try {
      setError(null);
      await traditionalSignup({
        name,
        email,
        password,
        marketingOptIn,
        locale
      });
      onTraditionalSignupSuccess();
    } catch (e) {
      setError(trans('signup.error'));
      console.error(e);
    }
  };

  const handleForgottenPassword = async ({ email }) => {
    try {
      setError(null);
      await traditionalForgotPassword({ email });
    } catch (err) {
      setError(trans('forgotten-password.error'));
      console.error(err);
    }
  };

  const handleHelpFormSubmit = values => {
    const { name, email, phoneNumber, comment } = values;

    setError(null);
    return createZendeskTicket(graphqlClient())({
      input: {
        reason: ZENDESK_SUPPORT.TENANT_REASONS.TENANT_ACCOUNT_ACCESS,
        role: 'tenant',
        name,
        email,
        phoneNumber,
        comment
      }
    })
      .then(() => {
        scrollTop();
      })
      .catch(err => {
        console.log(err);
      });
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(document.location.search);
    if (queryParams.get('target') === 'recover') {
      const initialEmail = queryParams.get('email');
      Mediator.publish('AUTH_MODAL_OPEN', {
        initialAction: 'forgotten_password',
        initialValues: {
          email: initialEmail
        },
        events: {
          open: {
            eventCategory: 'Facebook recovery',
            eventAction: 'Open recover password',
            eventLabel: 'open'
          },
          close: {
            eventCategory: 'Facebook recovery',
            eventAction: 'Close recover password',
            eventLabel: 'close'
          }
        }
      });
    }
  }, []);

  return (
    <LazyLoginSignUpModal
      locale={locale}
      onTraditionalLogin={handleTraditionalLogin}
      onTraditionalSignup={handleTraditionalSignup}
      onForgottenPassword={handleForgottenPassword}
      onHelpFormSubmit={handleHelpFormSubmit}
      error={error}
      hideTraditionalAuth={hideTraditionalAuth}
      hideFacebookOauth={hideFacebookOauth}
      hideGoogleOauth={hideGoogleOauth || deviceViewType === 'embedded-browser'}
    />
  );
};

LoginModal.propTypes = {
  onTraditionalLoginSuccess: PropTypes.func,
  onTraditionalSignupSuccess: PropTypes.func,
  deviceViewType: PropTypes.string
};

export default LoginModal;
